// src/components/layout/AppLayout.tsx
// Main application layout.
// Conditionally renders SuiteShell (new) or legacy layout based on
// meta.features.uiShell from Central switch-context.
//
// ARCHITECTURE: CRM is a CONSUMER only. Central controls uiShell feature flag.
// Shell is now consumed from @levelup/ui-shell package.

import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { isShellEnabled } from '@levelup/ui-shell';
import { useGetOnboardingStateQuery } from '../../api/onboardingApi';
import { useAuth } from '../../hooks/useAuth';
import { AUTH_MODE } from '../../config/centralAuth';
import { isCentralMode as computeIsCentralMode } from '../../lib/auth/isCentralMode';
import { useActiveWorkspaceId } from '../../hooks/useActiveWorkspace';
import { useAppSelector } from '../../store/store';
import { selectBootMeta } from '../../store/slices/bootContextSlice';
import { RealtimeProvider } from '../../realtime';
import TopBar from './TopBar';
import SideMenu from './SideMenu';
import CommandPalette from '../command/CommandPalette';
import LoadingSpinner from '../common/LoadingSpinner';
import { CRMSuiteShell } from '../../shell/SuiteShellAdapter';
// H-2C2 Phase 2.B step 2.B — onboarding overlay (gated behind
// VITE_ONBOARDING_OVERLAY flag, default OFF). When the flag is unset, both
// the provider and the overlay component are inert: the overlay's first
// line returns null, and the provider's only state is an unused boolean.
// See `components/onboarding/OnboardingOverlay.tsx` for the gate + invariants.
import { OnboardingOverlayProvider } from '../../context/OnboardingOverlayContext';
import OnboardingOverlay from '../onboarding/OnboardingOverlay';

interface AppLayoutProps {
  title?: string;
}

export default function AppLayout({ title }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authSource } = useAuth();
  const meta = useAppSelector(selectBootMeta);
  const activeWorkspaceId = useActiveWorkspaceId();

  // H-2C2 — central-mode predicate. Originally inlined here AND in
  // OnboardingWizardPage; consolidated into `lib/auth/isCentralMode.ts` in
  // Phase 2.B step 1 to prevent the predicate drift class that PRs #23,
  // #24, #26 fought through. See that file's block-comment for the full
  // historical context.
  const isCentralMode = computeIsCentralMode(AUTH_MODE, authSource);

  // Check if Suite UI Shell is enabled using the shared utility.
  //
  // H-2C2 — RP/BFF shell-predicate parity. `@levelup/ui-shell`'s
  // `isShellEnabled` only knows about `authSource`; it returns false when
  // `authSource !== 'central'`. RP/BFF users have `authSource === null`
  // (or 'legacy' from a stale stripe) because Central auth is server-side
  // via `crm_sid` and `rpSessionAuthBridge` — no browser-side Central
  // token populates Redux. Same root-cause class as PR #23's onboarding
  // parity fix, but on the shell predicate.
  //
  // Normalize `authSource` locally to match the canonical central-mode
  // predicate this file (line 46) and `OnboardingWizardPage.tsx:73` use.
  // Touches only this file — keeps the versioned `@levelup/ui-shell`
  // package interface unchanged.
  const forceUiShell = import.meta.env.VITE_FORCE_UI_SHELL === 'true';
  const shellAuthSource = isCentralMode ? 'central' : authSource;
  const isUiShellEnabled = isShellEnabled({
    authSource: shellAuthSource,
    meta,
    forceUiShell,
  });

  // Check onboarding state (legacy only — BootGate handles Central mode validation)
  const { data: onboardingState, isLoading: isLoadingOnboarding } = useGetOnboardingStateQuery(undefined, {
    skip: isCentralMode,
  });

  // Redirect to onboarding if not completed (legacy mode only)
  // In Central mode, BootGate validates workspace existence via switch-context.
  useEffect(() => {
    // Central mode: BootGate already ensures a valid workspace exists.
    // No workspace query or onboarding redirect needed here.
    if (isCentralMode) return;

    // Legacy mode: use onboarding state
    if (!isLoadingOnboarding && onboardingState && !onboardingState.isCompleted) {
      if (location.pathname !== '/workspaces') {
        navigate('/onboarding');
      }
    }
  }, [isCentralMode, onboardingState, isLoadingOnboarding, navigate, location.pathname]);

  // Show loading while checking onboarding (legacy mode only)
  if (!isCentralMode && isLoadingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Render Suite UI Shell if enabled by Central (from @levelup/ui-shell package)
  if (isUiShellEnabled) {
    return (
      <RealtimeProvider workspaceId={activeWorkspaceId}>
        <CRMSuiteShell />
        <CommandPalette />
        {/*
          H-2C2 Phase 2.B step 2.B — onboarding overlay. Sibling of the
          shell + command palette (existing "above-shell" pattern). Provider
          is narrow-scoped to wrap just the overlay. When
          VITE_ONBOARDING_OVERLAY !== 'true' the overlay's first line
          returns null and the provider's state is unused — net DOM impact
          zero. /onboarding standalone route stays untouched (mounted
          outside AppLayout — see AppRouter.tsx).
        */}
        <OnboardingOverlayProvider>
          <OnboardingOverlay />
        </OnboardingOverlayProvider>
      </RealtimeProvider>
    );
  }

  // Legacy layout (fallback when uiShell is not enabled)
  return (
    <RealtimeProvider workspaceId={activeWorkspaceId}>
      <div className="min-h-screen bg-gray-50">
        <TopBar title={title} />
        <SideMenu />

        <main className="pt-14 pb-4">
          <div className="container mx-auto px-4 py-4">
            <Outlet />
          </div>
        </main>
        <CommandPalette />
        {/* H-2C2 Phase 2.B step 2.B — mirrors the shell branch above (see
            comment there for the full rationale). */}
        <OnboardingOverlayProvider>
          <OnboardingOverlay />
        </OnboardingOverlayProvider>
      </div>
    </RealtimeProvider>
  );
}
